import { API_BASE_URL } from "../../../config";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
    balanceOf, fullCloseQty, mergeDrivers, signed, tone, isZero, validateSettlement, validateAdjustment,
} from './bagCreditCalc';
import { matchesPermission } from '../../Utils/userAuthority';

// หน้า "เครดิตกระสอบ" ของบัญชี — ยอดกระสอบขาด/เกินสะสมรายคนขับ (Vector 2026-10-07 tg_11548/11553/11556)
// ลบ = ขาด (แดง) · บวก = เกิน (เขียว) · แยกชนิด เขียว = เชือกเขียว/หลอดใหญ่ · แดง = เชือกแดง/บด · หน่วยกระสอบ ไม่คิดเงิน
// ขาด/เกินรายรอบมาจากสูตรเดิมของ bag-ledger (saledata BagLedgerSummaryService) — หน้านี้ไม่คำนวณเอง
// ยอดสะสม = ยอดยกมา (บัญชีปรับเอง) + Σ ขาด/เกินรายรอบ + Σ ที่ปิดแล้ว · เงินสดที่รับตอนปิดยอดบันทึกไว้เฉยๆ ไม่เข้าสูตร
// ไม่โชว์รหัสระบบ (uid) — โชว์ชื่อคนขับ · รอบเปิดไปหน้ารายงานรอบนั้น

const BASE = `${API_BASE_URL}/saledata/api/module/bag-credit`;
const REGISTRY = `${API_BASE_URL}/seller/api/driver-registry`;

const thaiDate = (iso) => {
    const [y, m, d] = String(iso || '').split('-').map(Number);
    return y ? `${d}/${m}/${y}` : '-';
};
const fmtDateTime = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const p = (x) => String(x).padStart(2, '0');
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const todayStr = () => {
    const d = new Date();
    const p = (x) => String(x).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const fmtMoney = (n) => (n == null ? '-' : Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const th = { background: '#f8f9fa', fontWeight: 700, borderBottom: '2px solid #E5E5E5', padding: '8px 12px', fontSize: '13px', color: '#495057', textAlign: 'left' };
const td = { borderBottom: '1px solid #E5E5E5', padding: '6px 12px', fontSize: '14px' };
const box = { border: '1px solid #E5E5E5', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px', background: '#fff' };
const inputStyle = { padding: '4px 8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '14px' };
const smallBtn = { padding: '3px 10px', fontSize: '13px' };

const LEDGER_SOURCE = { SYSTEM: 'ระบบคำนวณ', MANUAL: 'ปรับมือ (บัญชี)', SETTLEMENT: 'ปิดยอด' };

// ตัวเลขสองชนิดในช่องเดียว: เขียว / แดง
const Qty = ({ q, plus = true, testid }) => {
    const t = Number(q?.tube) || 0;
    const c = Number(q?.crushed) || 0;
    const f = (n) => (plus ? signed(n) : String(n));
    return (
        <span data-testid={testid} style={{ whiteSpace: 'nowrap' }}>
            <span style={{ color: tone(t) }}>เขียว {f(t)}</span>
            <span style={{ color: '#adb5bd' }}> · </span>
            <span style={{ color: tone(c) }}>แดง {f(c)}</span>
        </span>
    );
};

// ปุ่มเขียน (ปรับ/ปิดยอด) เห็นเมื่อมี report:write · ไม่รู้สิทธิ์ (session เก่าไม่มี permissions) = ให้ server ตัดสินเหมือนเดิม
const canWriteNow = () => {
    try {
        const perms = JSON.parse(localStorage.getItem('user') || '{}')?.permissions;
        return !Array.isArray(perms) || perms.some(g => matchesPermission(g, 'report:write'));
    } catch (e) { return true; }
};

const authHeaders = (token, json) => ({ Authorization: `Bearer ${token}`, ...(json ? { 'Content-Type': 'application/json' } : {}) });

const BagCredit = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const driverUid = searchParams.get('driver') || '';

    const [summary, setSummary] = useState(null);
    const [registry, setRegistry] = useState([]);
    const [registryError, setRegistryError] = useState('');
    const [detail, setDetail] = useState(null);
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [msg, setMsg] = useState('');
    const [busy, setBusy] = useState(false);

    const [panel, setPanel] = useState(null); // {type:'settle'|'adjust'|'unassigned', driver}
    const [form, setForm] = useState({});
    const [formError, setFormError] = useState('');
    const [unassigned, setUnassigned] = useState([]);
    const [picks, setPicks] = useState({});

    const canWrite = useMemo(canWriteNow, []);

    const getToken = useCallback(() => {
        const user = localStorage.getItem('user');
        if (!user) { navigate('/login'); return null; }
        return JSON.parse(user)?.token;
    }, [navigate]);

    const loadAll = useCallback(async () => {
        const token = getToken();
        if (!token) return;
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${BASE}/summary`, { headers: authHeaders(token) });
            if (!res.ok) {
                setError(res.status === 401 || res.status === 403 ? 'ไม่มีสิทธิ์เข้าถึงข้อมูลนี้' : `โหลดเครดิตกระสอบไม่สำเร็จ (HTTP ${res.status})`);
                setSummary(null);
            } else {
                setSummary(await res.json());
            }
        } catch (e) {
            setError('เชื่อมต่อเซิร์ฟเวอร์ไม่ได้');
            setSummary(null);
        }
        try {
            const r = await fetch(REGISTRY, { headers: authHeaders(token) });
            if (r.ok) { setRegistry(await r.json()); setRegistryError(''); }
            else setRegistryError('โหลดทะเบียนคนขับไม่สำเร็จ — แสดงเฉพาะคนที่มียอด');
        } catch (e) {
            setRegistryError('โหลดทะเบียนคนขับไม่สำเร็จ — แสดงเฉพาะคนที่มียอด');
        }
        setLoading(false);
    }, [getToken]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const loadDetail = useCallback(async () => {
        if (!driverUid) { setDetail(null); return; }
        const token = getToken();
        if (!token) return;
        const qs = new URLSearchParams();
        if (from) qs.set('from', from);
        if (to) qs.set('to', to);
        try {
            const res = await fetch(`${BASE}/drivers/${encodeURIComponent(driverUid)}${qs.toString() ? `?${qs}` : ''}`, { headers: authHeaders(token) });
            if (!res.ok) { setError(`โหลดรายละเอียดไม่สำเร็จ (HTTP ${res.status})`); setDetail(null); return; }
            setDetail(await res.json());
        } catch (e) {
            setError('เชื่อมต่อเซิร์ฟเวอร์ไม่ได้');
        }
    }, [driverUid, from, to, getToken]);

    useEffect(() => { loadDetail(); }, [loadDetail]);

    const drivers = useMemo(() => mergeDrivers(registry, summary?.drivers), [registry, summary]);
    const nameOf = useCallback((uid) => drivers.find(d => d.driverUid === uid)?.driverName || 'ไม่ทราบชื่อ', [drivers]);

    const post = async (path, body, okText) => {
        const token = getToken();
        if (!token || busy) return false;
        setBusy(true);
        setFormError('');
        try {
            const res = await fetch(`${BASE}${path}`, { method: 'POST', headers: authHeaders(token, true), body: JSON.stringify(body || {}) });
            if (!res.ok) {
                let text = '';
                try { text = (await res.json())?.error || ''; } catch (e) { /* ไม่มี body */ }
                setFormError(text || (res.status === 401 || res.status === 403 ? 'ไม่มีสิทธิ์ทำรายการนี้' : `ทำรายการไม่สำเร็จ (HTTP ${res.status})`));
                return false;
            }
            setMsg(okText);
            await loadAll();
            await loadDetail();
            return true;
        } catch (e) {
            setFormError('เชื่อมต่อเซิร์ฟเวอร์ไม่ได้');
            return false;
        } finally {
            setBusy(false);
        }
    };

    const openDriver = (uid) => { setMsg(''); setSearchParams({ driver: uid }); };
    const closeDriver = () => { setDetail(null); setSearchParams({}); setFrom(''); setTo(''); };

    const openSettle = (d) => {
        const full = fullCloseQty(d.balance);
        setForm({ tubeQty: full.tube, crushedQty: full.crushed, cashAmount: '', settledDate: todayStr(), note: '' });
        setFormError('');
        setPanel({ type: 'settle', driver: d });
    };
    const openAdjust = (d) => {
        setForm({ direction: 'add', tubeQty: 0, crushedQty: 0, note: '', effectiveDate: todayStr(), shiftLabel: '' });
        setFormError('');
        setPanel({ type: 'adjust', driver: d });
    };
    const openUnassigned = async () => {
        const token = getToken();
        if (!token) return;
        setFormError('');
        setPicks({});
        try {
            const res = await fetch(`${BASE}/unassigned`, { headers: authHeaders(token) });
            setUnassigned(res.ok ? await res.json() : []);
            if (!res.ok) setFormError(`โหลดรายการไม่สำเร็จ (HTTP ${res.status})`);
        } catch (e) {
            setFormError('เชื่อมต่อเซิร์ฟเวอร์ไม่ได้');
        }
        setPanel({ type: 'unassigned' });
    };

    const submitSettle = async () => {
        const d = panel.driver;
        const err = validateSettlement(form, d.balance);
        if (err) { setFormError(err); return; }
        const ok = await post('/settlements', {
            driverUid: d.driverUid, driverName: d.driverName,
            tubeQty: Number(form.tubeQty) || 0, crushedQty: Number(form.crushedQty) || 0,
            cashAmount: form.cashAmount === '' ? null : Number(form.cashAmount),
            settledDate: form.settledDate || todayStr(), note: form.note,
        }, `ปิดยอดของ ${d.driverName} แล้ว`);
        if (ok) setPanel(null);
    };
    const submitAdjust = async () => {
        const d = panel.driver;
        // ช่องกรอกเป็น "จำนวน" ล้วน ๆ — เพิ่ม/ลด เลือกด้วยปุ่ม แล้วค่อยแปลงเป็นเครื่องหมาย (เพิ่ม = เกิน +, ลด = ขาด −)
        const sign = form.direction === 'sub' ? -1 : 1;
        const signedForm = { ...form, tubeQty: sign * Math.abs(Number(form.tubeQty) || 0), crushedQty: sign * Math.abs(Number(form.crushedQty) || 0) };
        const err = validateAdjustment(signedForm);
        if (err) { setFormError(err); return; }
        const ok = await post('/adjustments', {
            driverUid: d.driverUid, driverName: d.driverName,
            tubeQty: signedForm.tubeQty, crushedQty: signedForm.crushedQty, note: form.note,
            effectiveDate: form.effectiveDate || todayStr(), shiftLabel: (form.shiftLabel || '').trim() || null,
        }, `บันทึกการปรับยอดของ ${d.driverName} แล้ว`);
        if (ok) setPanel(null);
    };
    const assign = async (round) => {
        const uid = picks[round.reportCode];
        if (!uid) { setFormError('เลือกคนขับก่อน'); return; }
        const ok = await post('/assign', { reportCode: round.reportCode, driverUid: uid, driverName: nameOf(uid) }, `ลงคนขับให้รอบ ${thaiDate(round.date)} แล้ว`);
        if (ok) {
            const token = getToken();
            const res = await fetch(`${BASE}/unassigned`, { headers: authHeaders(token) });
            setUnassigned(res.ok ? await res.json() : []);
        }
    };
    const unassignRound = async (r) => {
        if (!window.confirm(`ยกเลิกการลงคนขับของรอบ ${thaiDate(r.date)} ${r.route}?\nรอบนี้จะกลับไปเป็น "ไม่ระบุคนขับ"`)) return;
        const token = getToken();
        if (!token || busy) return;
        setBusy(true);
        try {
            const res = await fetch(`${BASE}/assign/${encodeURIComponent(r.reportCode)}`, { method: 'DELETE', headers: authHeaders(token) });
            if (!res.ok) { setError(`ยกเลิกการลงคนขับไม่สำเร็จ (HTTP ${res.status})`); return; }
            setMsg('ยกเลิกการลงคนขับแล้ว — รอบกลับไปเป็น "ไม่ระบุคนขับ"');
            await loadAll();
            await loadDetail();
        } catch (e) {
            setError('เชื่อมต่อเซิร์ฟเวอร์ไม่ได้');
        } finally {
            setBusy(false);
        }
    };
    const cancelSettlement = async (s) => {
        const note = window.prompt('เหตุผลที่ยกเลิกการปิดยอดนี้ (ไม่บังคับ) — ประวัติยังอยู่ ยอดจะกลับมา');
        if (note === null) return;
        await post(`/settlements/${s.id}/cancel`, { note }, 'ยกเลิกการปิดยอดแล้ว');
    };
    const voidAdjustment = async (a) => {
        const note = window.prompt('เหตุผลที่ยกเลิกการปรับยอดนี้ (ไม่บังคับ) — ประวัติยังอยู่ ไม่นับในยอด');
        if (note === null) return;
        await post(`/adjustments/${a.id}/void`, { note }, 'ยกเลิกการปรับยอดแล้ว');
    };

    const setF = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    // ---------- แผงกรอก (ปิดยอด / ปรับยอด / ลงคนขับ) ----------
    const renderPanel = () => {
        if (!panel) return null;
        const wrap = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
        const card = { background: '#fff', borderRadius: '10px', padding: '18px 20px', width: panel.type === 'unassigned' ? 'min(820px, 96vw)' : 'min(460px, 96vw)', maxHeight: '88vh', overflow: 'auto' };
        const label = { display: 'block', fontSize: '13px', color: '#495057', margin: '10px 0 3px' };
        return (
            <div style={wrap} data-testid="bag-credit-panel">
                <div style={card}>
                    {panel.type === 'settle' && (
                        <>
                            <h3 style={{ marginTop: 0 }}>ปิดยอดกระสอบ — {panel.driver.driverName}</h3>
                            <div style={{ fontSize: '13px', color: '#6c757d' }}>คงเหลือตอนนี้: <Qty q={panel.driver.balance} /> · ปิดได้เฉพาะส่วนที่ขาด</div>
                            <label style={label}>จำนวนกระสอบที่ปิด — เชือกเขียว</label>
                            <input aria-label="ปิด เชือกเขียว" type="number" min="0" style={inputStyle} value={form.tubeQty} onChange={setF('tubeQty')} />
                            <label style={label}>จำนวนกระสอบที่ปิด — เชือกแดง</label>
                            <input aria-label="ปิด เชือกแดง" type="number" min="0" style={inputStyle} value={form.crushedQty} onChange={setF('crushedQty')} />
                            <label style={label}>เงินสดที่รับ (บาท, ไม่บังคับ)</label>
                            <input aria-label="เงินสดที่รับ" type="number" min="0" step="0.01" style={inputStyle} value={form.cashAmount} onChange={setF('cashAmount')} />
                            <label style={label}>วันที่</label>
                            <input aria-label="วันที่ปิดยอด" type="date" style={inputStyle} value={form.settledDate} onChange={setF('settledDate')} />
                            <label style={label}>หมายเหตุ (ไม่บังคับ)</label>
                            <input aria-label="หมายเหตุปิดยอด" type="text" style={{ ...inputStyle, width: '100%' }} value={form.note} onChange={setF('note')} />
                        </>
                    )}
                    {panel.type === 'adjust' && (
                        <>
                            <h3 style={{ marginTop: 0 }}>ปรับยอดด้วยมือ / ยอดยกมา — {panel.driver.driverName}</h3>
                            <div style={{ fontSize: '13px', color: '#6c757d' }}>เลือก <b>เพิ่ม</b> (เกิน +) หรือ <b>ลด</b> (ขาด −) แล้วใส่จำนวนกระสอบ · เช่น ยกมาขาด 3 = ลด 3 · ระบบไม่แก้ข้อมูลที่คำนวณไว้ แต่เพิ่มเป็นรายการปรับมือแยกให้ตรวจย้อนได้</div>
                            <div role="radiogroup" aria-label="ทิศทางการปรับ" style={{ display: 'flex', gap: '8px', margin: '8px 0' }}>
                                <button type="button" role="radio" aria-checked={form.direction !== 'sub'} className="btc-btn" style={{ ...smallBtn, background: form.direction !== 'sub' ? '#2e7d32' : '#6B6B6B' }} onClick={() => setForm(f => ({ ...f, direction: 'add' }))}>＋ เพิ่ม (เกิน)</button>
                                <button type="button" role="radio" aria-checked={form.direction === 'sub'} className="btc-btn" style={{ ...smallBtn, background: form.direction === 'sub' ? '#E60012' : '#6B6B6B' }} onClick={() => setForm(f => ({ ...f, direction: 'sub' }))}>－ ลด (ขาด)</button>
                            </div>
                            <label style={label}>เชือกเขียว</label>
                            <input aria-label="ปรับ เชือกเขียว" type="number" min="0" style={inputStyle} value={form.tubeQty} onChange={setF('tubeQty')} />
                            <label style={label}>เชือกแดง</label>
                            <input aria-label="ปรับ เชือกแดง" type="number" min="0" style={inputStyle} value={form.crushedQty} onChange={setF('crushedQty')} />
                            <label style={label}>มีผลวันที่</label>
                            <input aria-label="วันที่มีผลของการปรับ" type="date" style={inputStyle} value={form.effectiveDate} onChange={setF('effectiveDate')} />
                            <label style={label}>กะ (ไม่บังคับ)</label>
                            <input aria-label="กะที่ปรับ" type="text" placeholder="เช่น กะเช้า / กะ #123" style={{ ...inputStyle, width: '100%' }} value={form.shiftLabel} onChange={setF('shiftLabel')} />
                            <label style={label}>หมายเหตุ (บังคับ)</label>
                            <input aria-label="หมายเหตุปรับยอด" type="text" style={{ ...inputStyle, width: '100%' }} value={form.note} onChange={setF('note')} />
                        </>
                    )}
                    {panel.type === 'unassigned' && (
                        <>
                            <h3 style={{ marginTop: 0 }}>รอบที่ไม่ระบุคนขับ</h3>
                            <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '8px' }}>เลือกคนขับให้แต่ละรอบ — ระบบเก็บว่าใครเลือก เมื่อไหร่ · ยอดจะย้ายเข้าคนที่เลือกทันที</div>
                            {unassigned.length === 0 ? <div>ไม่มีรอบที่ต้องลงคนขับแล้ว</div> : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead><tr><th style={th}>วันที่</th><th style={th}>สาย</th><th style={th}>ขาด/เกิน</th><th style={th}>คนขับ</th><th style={th}></th></tr></thead>
                                    <tbody>
                                        {unassigned.map(r => (
                                            <tr key={r.reportCode}>
                                                <td style={td}>{thaiDate(r.date)}</td>
                                                <td style={td}><Link to={`/saleDataDetail/${encodeURIComponent(r.reportCode)}`}>{r.route}</Link></td>
                                                <td style={td}><Qty q={r.variance} /></td>
                                                <td style={td}>
                                                    <select aria-label={`คนขับของรอบ ${thaiDate(r.date)} ${r.route}`} style={inputStyle} value={picks[r.reportCode] || ''}
                                                        onChange={e => setPicks(p => ({ ...p, [r.reportCode]: e.target.value }))}>
                                                        <option value="">— เลือกคนขับ —</option>
                                                        {drivers.filter(d => d.registered).map(d => <option key={d.driverUid} value={d.driverUid}>{d.driverName}</option>)}
                                                    </select>
                                                </td>
                                                <td style={td}><button className="btc-btn" style={smallBtn} disabled={busy} onClick={() => assign(r)}>ลงคนขับ</button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </>
                    )}
                    {formError && <div role="alert" style={{ color: '#c0392b', marginTop: '10px', fontSize: '13px' }}>{formError}</div>}
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
                        <button className="btc-btn" style={{ background: '#6B6B6B' }} onClick={() => { setPanel(null); setFormError(''); }}>{panel.type === 'unassigned' ? 'ปิด' : 'ยกเลิก'}</button>
                        {panel.type === 'settle' && <button className="btc-btn" disabled={busy} onClick={submitSettle}>บันทึกการปิดยอด</button>}
                        {panel.type === 'adjust' && <button className="btc-btn" disabled={busy} onClick={submitAdjust}>บันทึก</button>}
                    </div>
                </div>
            </div>
        );
    };

    // ---------- รายละเอียดรายคน ----------
    const renderDetail = () => {
        const d = drivers.find(x => x.driverUid === driverUid) || (detail && { ...detail.driver, driverName: nameOf(driverUid) });
        if (!d) return null;
        const bal = detail?.driver?.balance || d.balance;
        return (
            <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <button className="btc-btn" style={{ background: '#6B6B6B', ...smallBtn }} onClick={closeDriver}>← กลับรายชื่อ</button>
                    <h3 style={{ margin: 0 }}>เครดิตกระสอบ — {d.driverName}</h3>
                </div>
                <div style={box}>
                    <div>คงเหลือ: <b><Qty q={bal} testid="detail-balance" /></b></div>
                    <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '4px' }}>
                        ยอดยกมา <Qty q={detail?.driver?.opening} /> · ขาด/เกินรายรอบ <Qty q={detail?.driver?.variance} /> · ปิดแล้ว <Qty q={detail?.driver?.closed} plus={false} />
                    </div>
                    <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
                        {canWrite && <button className="btc-btn" onClick={() => openSettle({ ...d, balance: bal })} disabled={isZero(fullCloseQty(bal))}>ปิดยอด</button>}
                        {canWrite && <button className="btc-btn" style={{ background: '#6B6B6B' }} onClick={() => openAdjust(d)}>ปรับยอดด้วยมือ / ยอดยกมา</button>}
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
                    <b>รายรอบ</b>
                    <label style={{ fontSize: '13px' }}>ตั้งแต่ <input aria-label="ตั้งแต่วันที่" type="date" style={inputStyle} value={from} onChange={e => setFrom(e.target.value)} /></label>
                    <label style={{ fontSize: '13px' }}>ถึง <input aria-label="ถึงวันที่" type="date" style={inputStyle} value={to} onChange={e => setTo(e.target.value)} /></label>
                    {(from || to) && <span style={{ fontSize: '13px' }}>รวมในช่วงนี้: <Qty q={detail?.rangeVariance} /></span>}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '18px' }}>
                    <thead><tr><th style={th}>วันที่</th><th style={th}>สาย</th><th style={th}>ขาด/เกิน</th><th style={th}>ที่มาของคนขับ</th><th style={th}></th></tr></thead>
                    <tbody>
                        {(detail?.rounds || []).length === 0 && <tr><td style={td} colSpan={5}>ไม่มีรอบในช่วงนี้</td></tr>}
                        {(detail?.rounds || []).map(r => (
                            <tr key={r.reportCode}>
                                <td style={td}>{thaiDate(r.date)}</td>
                                <td style={td}><Link to={`/saleDataDetail/${encodeURIComponent(r.reportCode)}`}>{r.route}</Link></td>
                                <td style={td}><Qty q={r.variance} /></td>
                                <td style={td}>{r.driverSource === 'IMD' ? 'คนขับเลือกตอนส่งเงิน' : 'บัญชีลงให้'}</td>
                                <td style={td}>{r.driverSource === 'ACCOUNTING' && <button className="btc-btn" style={{ ...smallBtn, background: '#6B6B6B' }} disabled={busy} onClick={() => unassignRound(r)}>ยกเลิกการลงคนขับ</button>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <b>ประวัติปิดยอด</b>
                <table style={{ width: '100%', borderCollapse: 'collapse', margin: '6px 0 18px' }}>
                    <thead><tr><th style={th}>วันที่</th><th style={th}>กระสอบที่ปิด</th><th style={th}>เงินสดที่รับ</th><th style={th}>หมายเหตุ</th><th style={th}>บันทึกเมื่อ</th><th style={th}></th></tr></thead>
                    <tbody>
                        {(detail?.settlements || []).length === 0 && <tr><td style={td} colSpan={6}>ยังไม่เคยปิดยอด</td></tr>}
                        {(detail?.settlements || []).map(s => (
                            <tr key={s.id} style={s.cancelled ? { color: '#adb5bd', textDecoration: 'line-through' } : undefined}>
                                <td style={td}>{thaiDate(s.settledDate)}</td>
                                <td style={td}><Qty q={s.qty} plus={false} /></td>
                                <td style={td}>{fmtMoney(s.cashAmount)}</td>
                                <td style={td}>{s.note || '-'}{s.cancelled ? ` (ยกเลิก: ${s.cancelNote || '-'})` : ''}</td>
                                <td style={td}>{fmtDateTime(s.createdAt)}</td>
                                <td style={td}>{!s.cancelled && <button className="btc-btn" style={{ ...smallBtn, background: '#6B6B6B' }} disabled={busy} onClick={() => cancelSettlement(s)}>ยกเลิกการปิด</button>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <b>สมุดบัญชีคนขับ — ทุกรายการ (วันที่ · กะ · เพิ่ม/ลด · ยอดสะสม)</b>
                <div style={{ fontSize: '12px', color: '#6c757d', margin: '2px 0 6px' }}>ระบบคำนวณจากรอบ + ที่บัญชีปรับเอง + ปิดยอด · แถวที่ขีดฆ่า = ยกเลิกแล้ว (ไม่นับในยอดสะสม) · ใหม่สุดอยู่บน</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', margin: '6px 0 18px' }}>
                    <thead><tr><th style={th}>วันที่</th><th style={th}>กะ / สาย</th><th style={th}>ที่มา</th><th style={th}>เพิ่ม / ลด</th><th style={th}>ยอดสะสม</th><th style={th}>ผู้ปรับ · เหตุผล</th><th style={th}></th></tr></thead>
                    <tbody data-testid="bag-credit-ledger">
                        {(detail?.ledger || []).length === 0 && <tr><td style={td} colSpan={7}>ยังไม่มีรายการ</td></tr>}
                        {(detail?.ledger || []).map((r, i) => (
                            <tr key={`${r.source}-${r.refId || i}-${i}`} style={r.voided ? { color: '#adb5bd', textDecoration: 'line-through' } : (r.source === 'MANUAL' ? { background: '#fff8e1' } : undefined)}>
                                <td style={td}>{thaiDate(r.date)}</td>
                                <td style={td}>{r.shiftLabel || '-'}</td>
                                <td style={td}><b>{LEDGER_SOURCE[r.source] || r.source}</b></td>
                                <td style={td}><Qty q={r.qty} plus={r.source !== 'SETTLEMENT'} /></td>
                                <td style={td}><Qty q={r.runningBalance} /></td>
                                <td style={td}>{r.source === 'SYSTEM' ? '-' : <>{r.actor || '-'}{r.note ? <> · <span>{r.note}</span></> : null}</>}</td>
                                <td style={td}>
                                    {r.source === 'MANUAL' && !r.voided && <button className="btc-btn" style={{ ...smallBtn, background: '#6B6B6B' }} disabled={busy} onClick={() => voidAdjustment({ id: r.refId })}>ยกเลิกการปรับ</button>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    // ---------- หน้าหลัก ----------
    return (
        <div className="btc-container" style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                <h2 style={{ margin: 0 }}>เครดิตกระสอบ</h2>
                <button className="btc-btn" style={{ background: '#6B6B6B', ...smallBtn }} onClick={loadAll}>รีเฟรช</button>
            </div>
            {error && <div role="alert" style={{ ...box, borderColor: '#c0392b', color: '#c0392b' }}>{error}</div>}
            {registryError && <div style={{ ...box, color: '#8a6d3b', background: '#fcf8e3' }}>{registryError}</div>}
            {msg && <div style={{ ...box, color: '#1e8449' }}>{msg}</div>}
            {loading && <div>กำลังโหลด...</div>}

            {!loading && summary && !driverUid && (
                <>
                    <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '10px' }}>
                        ยอดกระสอบขาด/เกินสะสมรายคนขับ นับจากรอบตั้งแต่ {thaiDate(summary.startDate)} · ลบ = ขาด (แดง) · บวก = เกิน (เขียว) เก็บไว้หักกับครั้งที่ขาด
                    </div>
                    {summary.unassignedRounds > 0 && (
                        <div style={{ ...box, background: '#fcf8e3', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }} data-testid="unassigned-box">
                            <span>รอบที่ไม่ระบุคนขับ <b>{summary.unassignedRounds}</b> รอบ · รวม <Qty q={summary.unassignedVariance} /></span>
                            <button className="btc-btn" style={smallBtn} onClick={openUnassigned}>เลือกคนขับ</button>
                        </div>
                    )}
                    {summary.unresolvedRounds > 0 && (
                        <div style={{ ...box, background: '#fcf8e3', color: '#8a6d3b' }}>
                            มี {summary.unresolvedRounds} รอบที่คำนวณไม่ได้ (หาสายหรือคนขับไม่ได้ชั่วคราว) — ยังไม่นับในยอดด้านล่าง ลองรีเฟรชอีกครั้ง
                        </div>
                    )}
                    <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="bag-credit-table">
                        <thead><tr><th style={th}>คนขับ</th><th style={th}>ยอดยกมา</th><th style={th}>ขาด/เกินสะสม</th><th style={th}>ปิดแล้ว</th><th style={th}>คงเหลือ</th><th style={th}></th></tr></thead>
                        <tbody>
                            {drivers.length === 0 && <tr><td style={td} colSpan={6}>ยังไม่มีคนขับในทะเบียน</td></tr>}
                            {drivers.map(d => {
                                const bal = d.balance || balanceOf(d);
                                return (
                                    <tr key={d.driverUid}>
                                        <td style={td}><a href={`?driver=${encodeURIComponent(d.driverUid)}`} style={{ textDecoration: 'underline', fontWeight: 600 }} title="กดดูรายละเอียด วันที่/กะ/เพิ่ม-ลด" onClick={(e) => { e.preventDefault(); openDriver(d.driverUid); }}>{d.driverName}</a></td>
                                        <td style={td}><Qty q={d.opening} /></td>
                                        <td style={td}><Qty q={d.variance} /></td>
                                        <td style={td}><Qty q={d.closed} plus={false} /></td>
                                        <td style={td}><b><Qty q={bal} /></b></td>
                                        <td style={td}>
                                            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                <button className="btc-btn" style={{ ...smallBtn, background: '#6B6B6B' }} data-testid="row-detail" onClick={() => openDriver(d.driverUid)}>รายละเอียด</button>
                                                {canWrite && <button className="btc-btn" style={{ ...smallBtn, background: '#2e7d32' }} data-testid="row-adjust" onClick={() => openAdjust({ ...d, balance: bal })}>ปรับยอด</button>}
                                                {canWrite && <button className="btc-btn" style={smallBtn} disabled={isZero(fullCloseQty(bal))} onClick={() => openSettle({ ...d, balance: bal })}>ปิดยอด</button>}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}
            {!loading && driverUid && renderDetail()}
            {renderPanel()}
        </div>
    );
};

export default BagCredit;
